import bigInt from 'big-integer';
import SgfUtil from './sgf/sgfUtil';
import zobristHash from './zobristHash';

class BoardModel {

  static BLACK = 1;
  static EMPTY = 0;
  static WHITE = -1;
  static OUTSIDE = -2;

  constructor(boardSize) {
    this.boardSize = boardSize;
    this.states = undefined;
    this.deadBlack = 0;
    this.deadWhite = 0;
    this.blackIsNext = true;
    this.moveNumber = 0;
    this.lastMove = undefined;
    this.validate = true;
    this.deadCheckList = undefined;
    this.hash = bigInt.zero;
    this.initializeStates();
  }

  initializeStates() {
    this.states = Array.from(
      new Array(this.boardSize + 2), 
      () => new Array(this.boardSize + 2)
      .fill(BoardModel.EMPTY)); // 19x19 board -> 21x21 states
    this.states[0] = this.states[0].fill(BoardModel.OUTSIDE);
    this.states[this.boardSize + 1] = this.states[this.boardSize + 1].fill(BoardModel.OUTSIDE);
    for (let x = 1; x <= this.boardSize; x++) {
      this.states[x][0] = BoardModel.OUTSIDE;
      this.states[x][this.boardSize + 1] = BoardModel.OUTSIDE;
    }
  }

  clone() {
    const bm = new BoardModel(this.boardSize);
    bm.states = Array.from(this.states, row => Array.from(row));
    bm.deadBlack = this.deadBlack;
    bm.deadWhite = this.deadWhite;
    bm.blackIsNext = this.blackIsNext;
    bm.moveNumber = this.moveNumber;
    bm.lastMove = this.lastMove;
    bm.validate = this.validate;
    bm.hash = this.hash;
    return bm;
  }

  /**
   * Returns the state.
   *
   * @param {int} x
   * @param {int} y
   * @return the state
   */
  getState(x, y) {
    return this.states[x][y];
  }

  /**
   * Returns the state at index.
   * 
   * @param {number} index 
   */
  getStateAtIndex(index) {
    const x = index % this.boardSize + 1;
    const y = Math.floor(index / this.boardSize) + 1;
    return this.states[x][y];
  }

  /**
   * Returns the state of the upper side.
   *
   * @param {int} x
   * @param {int} y
   * @return the state of the upper side
   */
  getUpperState(x, y) {
    return (y <= 1) ? BoardModel.OUTSIDE : this.states[x][y - 1];
  }

  /**
   * Returns the state of the lower side.
   *
   * @param {int} x
   * @param {int} y
   * @return the state of the lower side
   */
  getLowerState(x, y) {
    return (y >= this.boardSize) ? BoardModel.OUTSIDE : this.states[x][y + 1];
  }

  /**
   * Returns the state of the left side.
   *
   * @param {int} x
   * @param {int} y
   * @return the state of the left side
   */
  getLeftState(x, y) {
    return (x <= 1) ? BoardModel.OUTSIDE : this.states[x - 1][y];
  }

  /**
   * Returns the state of the right side.
   *
   * @param {int} x
   * @param {int} y
   * @return the state of the right side
   */
  getRightState(x, y) {
    return (x >= this.boardSize) ? BoardModel.OUTSIDE : this.states[x + 1][y];
  }

  /**
   * Sets the state at (x, y). This is a private function.
   * Use moveBlack/White or addBlack/White/Empty.
   * 
   * @param x     x
   * @param y     y
   * @param state state
   * @throws OutOfBoardSizeException if out of board size.
   * @throws IllegalMoveException    if illegal move.
   */
  setState(x, y, state) {
    this.checkRange(x, y);
    if (this.validate && this.states[x][y]) {
      throw new IllegalMoveException(`Not empty: ${x},${y} ${this.states[x][y]}`);
    }
    this.states[x][y] = state;
    this.checkDeadAround(x, y, state);
    if (this.validate) {
      this.checkLegalMove(x, y, state);
    }
  }

  /**
   * Adds a stone at (x, y) without validation. This is a private function.
   * Use addBlack/White/Empty.
   *
   * @param x     x
   * @param y     y
   * @param state the state to set
   * @throws OutOfBoardSizeException if invalid position
   */
  addStone(x, y, state) {
    this.checkRange(x, y);
    this.states[x][y] = state;
  }

  /**
   * Checks the position range.
   * 
   * @param x x
   * @param y y
   * @throws RangeError if out of board size
   */
  checkRange(x, y) {
    if (x < 1 || x > this.boardSize || y < 1 || y > this.boardSize) {
      throw new RangeError();
    }
  }

  /**
   * Checks stones around the specified position are dead. If dead, removes
   * them.
   *
   * @param state State
   * @param x x
   * @param y y
   */
  checkDeadAround(x, y, state) {
    const opp = state * -1;
    this.checkDead(x - 1, y, opp); // left
    this.checkDead(x + 1, y, opp); // right
    this.checkDead(x, y - 1, opp); // upper
    this.checkDead(x, y + 1, opp); // lower
  }

  checkLegalMove(x, y, state) {
    this.deadCheckList = new PointChecker(this.boardSize);
    if (!this.hasGroupLiberty(x, y, state)) {
      throw new IllegalMoveException(`No liberty: ${x},${y}`);
    }
    this.deadCheckList = null;
  }

  /**
   * Checks if the stones, which color are state, at the specified position
   * are dead. If dead, removes them.
   *
   * @param state State
   * @param x x
   * @param y y
   */
  checkDead(x, y, state) {
    this.deadCheckList = new PointChecker(this.boardSize);
    if (this.states[x][y] !== state) { // not opponent's stone
      return;
    } else if (!this.hasGroupLiberty(x, y, state)) {
      this.removeDeadStones(x, y, state);
    }
    this.deadCheckList = null;
  }

  hasGroupLiberty(x, y, state) {
    if (this.deadCheckList.isChecked(x, y)) {
      return false;
    }
    this.deadCheckList.check(x, y);
    if (this.states[x][y] !== state) {
      return false;
    } else if (this.hasLiberty(x, y)) {
      return true;
    } else if (this.hasGroupLiberty(x - 1, y, state)) {
      return true;
    } else if (this.hasGroupLiberty(x + 1, y, state)) {
      return true;
    } else if (this.hasGroupLiberty(x, y - 1, state)) {
      return true;
    } else if (this.hasGroupLiberty(x, y + 1, state)) {
      return true;
    }
    return false;
  }

  /**
   * Returns true if the position has any liberties.
   *
   * @param x x
   * @param y y
   * @return true if the position has any liberties
   */
  hasLiberty(x, y) {
    if (this.isEmpty(x - 1, y)) {
      return true;
    } else if (this.isEmpty(x + 1, y)) {
      return true;
    } else if (this.isEmpty(x, y - 1)) {
      return true;
    } else if (this.isEmpty(x, y + 1)) {
      return true;
    }
    return false;
  }

  /**
   * Returns true if the board state of the specified position is empty.
   *
   * @return true if the board state of the specified position is empty
   */
  isEmpty(x, y) {
    return (this.states[x][y] === BoardModel.EMPTY);
  }

  removeDeadStones(x, y, state) {
    if (this.states[x][y] !== state) {
      return;
    }
    this.addEmpty(x, y);
    const move = this.getIndex(x, y);
    if (state === BoardModel.BLACK) {
      this.deadBlack++;
      this.hash = this.hash.xor(bigInt(zobristHash[move][0]));
    } else if (state === BoardModel.WHITE) {
      this.deadWhite++;
      this.hash = this.hash.xor(bigInt(zobristHash[move][1]));
    }
    this.removeDeadStones(x - 1, y, state); // left
    this.removeDeadStones(x + 1, y, state); // right
    this.removeDeadStones(x, y - 1, state); // upper
    this.removeDeadStones(x, y + 1, state); // lower
  }

  getIndex(x, y) {
    return (x - 1) + (y - 1) * this.boardSize;
  }

  move(index) {    
    if (index === this.boardSize ** 2) {
      if (this.blackIsNext) {
        this.passBlack();
      } else {
        this.passWhite();
      }
    } else {   
      const vertex = SgfUtil.indexToVertex(index, this.boardSize);
      if (this.blackIsNext) {
        this.moveBlack(vertex[0], vertex[1]);
      } else {
        this.moveWhite(vertex[0], vertex[1]);
      }
    }
  }

  moveBlack(x, y) {
    if (!this.isPass(x, y)) {
      try {
        this.setState(x, y, BoardModel.BLACK);
        const move = this.getIndex(x, y);
        this.hash = this.hash.xor(bigInt(zobristHash[move][0]));
      } catch (e) {
        throw new IllegalMoveException(`Illegal move: ${x},${y}`);
      }
    }
    this.lastMove = [x, y];
    this.blackIsNext = false;
    this.moveNumber++;
  }

  moveWhite(x, y) {
    if (!this.isPass(x, y)) {
      try {
        this.setState(x, y, BoardModel.WHITE);
        const move = this.getIndex(x, y);
        this.hash = this.hash.xor(bigInt(zobristHash[move][1]));
      } catch (e) {
        throw new IllegalMoveException(`Illegal move: ${x},${y}`);
      }
    }
    this.lastMove = [x, y];
    this.blackIsNext = true;
    this.moveNumber++;
  }

  passBlack() {
    const move = this.boardSize ** 2;
    this.hash = this.hash.xor(bigInt(zobristHash[move][0]));
    this.lastMove = [this.boardSize + 1, this.boardSize + 1];
    this.blackIsNext = false;
    this.moveNumber++;
  }

  passWhite() {
    const move = this.boardSize ** 2;
    this.hash = this.hash.xor(bigInt(zobristHash[move][1]));
    this.lastMove = [this.boardSize + 1, this.boardSize + 1];
    this.blackIsNext = true;
    this.moveNumber++;
  }

  forceMove() {
    this.validate = false;
  }

  addBlack(x, y) {
    this.addStone(x, y, BoardModel.BLACK);
    const move = this.getIndex(x, y);
    this.hash = this.hash.xor(bigInt(zobristHash[move][0]));
  }

  addEmpty(x, y) {
    this.addStone(x, y, BoardModel.EMPTY);
  }

  addWhite(x, y) {
    this.addStone(x, y, BoardModel.WHITE);
    const move = this.getIndex(x, y);
    this.hash = this.hash.xor(bigInt(zobristHash[move][1]));
  }

  /**
   * Returns true if the specified point means 'pass', i.e [] or [tt] for
   * 19x19 board.
   *
   * @param x
   * @param y
   * @return true if the specified means 'pass'
   */
  isPass(x, y) {
    return (this.boardSize <= 19 && x === 20 && y === 20);
  }
  
  toString() {
    let str = "";
    str += "\n";
    str += "boardSize=" + this.boardSize;
    str += ", lastMove=" + this.lastMove;
    str += ", moveNumber=" + this.moveNumber;
    str += ", deadBlack=" + this.deadBlack;
    str += ", deadWhite=" + this.deadWhite;
    str += ", blackIsNext=" + this.blackIsNext;
    str += ", hash=" + this.hash;
    str += "\n";
    for (let y = 0; y < this.boardSize + 2; y++) {
      for (let x = 0; x < this.boardSize + 2; x++) {
        if (this.states[x][y] === BoardModel.EMPTY) {
          str += " .";
        } else if (this.states[x][y] === BoardModel.BLACK) {
          str += " X";
        } else if (this.states[x][y] === BoardModel.WHITE) {
          str += " O";
        }
      }
      str += "\n";
    }
    return str;
  }
}

class PointChecker {

  constructor(boardSize) {
    this.boardSize = boardSize;
    this.array = undefined;
    this.clear();
  }

  /**
   * Returns true if (x,y) is checked.
   * 
   * @param x x
   * @param y y
   * @return true if (x,y) is checked
   */
  isChecked(x, y) {
    return this.array[x][y];
  }

  /**
   * Sets (x,y) checked.
   * 
   * @param x x
   * @param y y
   */
  check(x, y) {
    this.array[x][y] = true;
  }

  /**
   * Sets (x,y) unchecked.
   * 
   * @param x x
   * @param y y
   */
  reset(x, y) {
    this.array[x][y] = false;
  }

  /**
   * Sets all points unchecked.
   */
  clear() {
    this.array = Array.from(
      new Array(this.boardSize + 2), () => new Array(this.boardSize + 2).fill(false));
  }
}

export class IllegalMoveException extends Error {

}

export default BoardModel;